// src/pages/accounts/IncomeLedger.js

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";

const STATUS_UI = {
  paid:            { label: "Paid",           bg: "#d1fae5", color: "#065f46" },
  payment_pending: { label: "Pending",        bg: "#fef3c7", color: "#92400e" },
};

export default function IncomeLedger() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || localStorage.getItem("company_id") || "";

  const [income,         setIncome]         = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter,   setStatusFilter]   = useState("");

  useEffect(() => { loadIncome(); }, [companyId]);

  const loadIncome = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      // ✅ where() query — income type only
      const snap = await getDocs(
        query(collection(db, "financial_transactions"),
          where("company_id", "==", companyId),
          where("type",       "==", "income"))
      );
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
      setIncome(list);
    } catch (err) {
      console.error("IncomeLedger error:", err);
    } finally { setLoading(false); }
  };

  const filtered = income.filter(item => {
    if (categoryFilter && item.sub_type !== categoryFilter && item.category !== categoryFilter) return false;
    if (statusFilter   && item.status !== statusFilter) return false;
    return true;
  });

  const totalIncome   = income.reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalPaid     = income.filter(i => i.status === "paid").reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalPending  = income.filter(i => i.status === "payment_pending").reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalFiltered = filtered.reduce((s, i) => s + Number(i.amount || 0), 0);

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <h1 className="text-xl font-bold text-slate-800 mb-5">Income Ledger</h1>

      {/* SUMMARY */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-green-600 text-white p-4 rounded-2xl shadow-sm">
          <p className="text-xs opacity-80">Total Income</p>
          <p className="text-xl font-black mt-0.5">₹{totalIncome.toLocaleString()}</p>
        </div>
        <div className="bg-blue-600 text-white p-4 rounded-2xl shadow-sm">
          <p className="text-xs opacity-80">Collected</p>
          <p className="text-xl font-black mt-0.5">₹{totalPaid.toLocaleString()}</p>
        </div>
        <div className="bg-amber-500 text-white p-4 rounded-2xl shadow-sm">
          <p className="text-xs opacity-80">Pending Collection</p>
          <p className="text-xl font-black mt-0.5">₹{totalPending.toLocaleString()}</p>
        </div>
      </div>

      {/* FILTERS */}
      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="border border-slate-200 p-2 rounded-xl text-sm bg-white outline-none">
          <option value="">All Types</option>
          <option value="invoice">Invoice</option>
          <option value="invoice_payment">Invoice Payment</option>
          <option value="service">Service</option>
        </select>

        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="border border-slate-200 p-2 rounded-xl text-sm bg-white outline-none">
          <option value="">All Status</option>
          <option value="paid">Paid</option>
          <option value="payment_pending">Pending</option>
        </select>

        <div className="ml-auto text-sm font-semibold text-slate-600">
          Showing: ₹{totalFiltered.toLocaleString()}
        </div>
      </div>

      {/* TABLE */}
      {loading ? (
        <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
      ) : filtered.length === 0 ? (
        <p className="text-slate-400 text-sm text-center mt-10">No income records found.</p>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Reference</th>
                  <th className="p-3 text-left">Client</th>
                  <th className="p-3 text-left">Site</th>
                  <th className="p-3 text-left">Description</th>
                  <th className="p-3 text-left">Mode</th>
                  <th className="p-3 text-right">Amount</th>
                  <th className="p-3 text-right">Balance</th>
                  <th className="p-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  const statusUI = STATUS_UI[item.status] || { label: item.status || "-", bg: "#f3f4f6", color: "#374151" };
                  return (
                    <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 text-slate-500 whitespace-nowrap">
                        {item.created_at?.seconds
                          ? new Date(item.created_at.seconds * 1000).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                          : "-"}
                      </td>
                      <td className="p-3 font-medium text-blue-600 text-xs">{item.reference_no || item.reference_id || "-"}</td>
                      <td className="p-3 text-slate-600">{item.client_name || "-"}</td>
                      <td className="p-3 text-slate-500 text-xs">{item.site_name || "-"}</td>
                      <td className="p-3 text-slate-600 max-w-[180px] truncate">{item.description || item.sub_type || "-"}</td>
                      <td className="p-3 text-slate-500 capitalize text-xs">{item.payment_mode || "-"}</td>
                      <td className="p-3 text-right font-semibold text-green-600">₹{Number(item.amount || 0).toLocaleString()}</td>
                      <td className="p-3 text-right text-red-500 text-xs">
                        {Number(item.balance_amount || 0) > 0
                          ? `₹${Number(item.balance_amount).toLocaleString()}`
                          : "—"}
                      </td>
                      <td className="p-3">
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                          style={{ background: statusUI.bg, color: statusUI.color }}>
                          {statusUI.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
